import * as Yup from 'yup';
import dayjs from 'dayjs';

export type NoteFormValues = {
  uuid: string;
  note: string | null;
  draft: boolean | null;
  noteType: string | null;
  clientReached: string | null;
  dateOfEncounter: string | null;
  startTimeOfEncounter: string | null;
  nextContact: string | null;
  typeOfSupport: string[];
  id: number | null;
  timeSpentMin: number | string | null;
  timeSpent?: number | string | null;
  siteId: number | null;
  zCodes: Array<{
    id: number;
    name?: string;
    explanations?: any[];
    selectedExplanations: string[];
  }>;
  whoDidYouReach: string[];
  whoWereYouTryingToReach: string;
  howDidYouTryThem: string;
  howDidYouReachThem: string | null;
  whatTypeOfPlaceWereYouIn: string;
  billingProviderId: string | null;
  otherProviderName: string;
  documentationTime: number | string | null;
  chwId: number | string | null;
  managerId: number | string | null;
  sitesEnabled?: boolean;
  isBillingEnabled?: boolean;
};

const isDateValidAndNotInFuture = (fieldVal: string): boolean => {
  const parsedDate = dayjs(fieldVal, 'MM/DD/YYYY', true);

  if (!parsedDate.isValid()) {
    return false;
  }

  return !parsedDate.isAfter(dayjs(), 'day');
};

export const validationSchema = Yup.object({
  note: Yup.string().required('Please enter a note to save.').nullable(),
  noteType: Yup.string().required('Note type is required.').nullable(),
  dateOfEncounter: Yup.string()
    .nullable()
    .when('noteType', {
      is: (noteType: string) => noteType === 'MANAGER_REVIEW',
      then: Yup.string()
        .nullable()
        .required('Date is required.')
        .test(
          'dateOfEncounter',
          "Date can't be in the future.",
          async (fieldVal): Promise<boolean | Yup.ValidationError | any> => {
            if (!fieldVal) return false;

            return isDateValidAndNotInFuture(fieldVal);
          }
        ),
      otherwise: Yup.string()
        .nullable()
        .required('Date of Encounter is required.')
        .test(
          'dateOfEncounter',
          "Date of Encounter can't be in the future.",
          async (fieldVal): Promise<boolean | Yup.ValidationError | any> => {
            if (!fieldVal) return false;

            return isDateValidAndNotInFuture(fieldVal);
          }
        )
    }),

  startTimeOfEncounter: Yup.string()
    .nullable()
    .when('noteType', {
      is: (noteType: string) => noteType !== 'MANAGER_REVIEW',
      then: Yup.string()
        .nullable()
        .test(
          'startTimeOfEncounter',
          'Start Time is required.',
          async (fieldVal, values): Promise<boolean | Yup.ValidationError | any> => {
            if (!fieldVal || fieldVal === 'Invalid Date') return false;

            return true;
          }
        ),
      otherwise: Yup.string().nullable().notRequired()
    }),
  nextContact: Yup.string()
    .nullable()
    .test(
      'nextContact',
      'Date of Next Contact must be after Date of Encounter.',
      async (fieldVal, values): Promise<boolean | Yup.ValidationError | any> => {
        const formikValues = values.parent;

        if (fieldVal == null || fieldVal === '') {
          return true;
        }

        if (formikValues.dateOfEncounter == null || formikValues.dateOfEncounter === '') {
          return true;
        }
        //check if end time after start time
        let isStartTimeBeforeEndTime = true;
        const startTime = dayjs(`${formikValues.dateOfEncounter}`);

        const endTime = dayjs(`${formikValues.nextContact}`);

        isStartTimeBeforeEndTime = startTime.isBefore(endTime);

        return isStartTimeBeforeEndTime;
      }
    ),

  typeOfSupport: Yup.array()
    .of(Yup.string())
    .when('noteType', {
      is: (noteType: string) => noteType !== 'MANAGER_REVIEW',
      then: Yup.array()
        .of(Yup.string())
        .min(1, 'Type of support is required.')
        .required('Type of support is required.'),
      otherwise: Yup.array().of(Yup.string()).notRequired()
    }),
  timeSpentMin: Yup.string().nullable().required('Time Spent (min) is required.'),
  documentationTime: Yup.string()
    .nullable()
    .required('Documentation Time (min) is required.')
    .test('documentationTime', 'Documentation Time (min) is required.', (value) => {
      return value !== null && value !== '' && parseInt(value ?? '0') > 0;
    }),
  whoDidYouReach: Yup.array()
    .of(Yup.string())
    .when('noteType', {
      is: (noteType: string) => noteType !== 'MANAGER_REVIEW',
      then: Yup.array()
        .of(Yup.string())
        .min(1, 'Who did you reach? is required.')
        .required('Who did you reach? is required.'),
      otherwise: Yup.array().of(Yup.string()).notRequired()
    }),
  howDidYouReachThem: Yup.string()
    .nullable()
    .when('whoDidYouReach', {
      is: (whoDidYouReach: string[]) =>
        whoDidYouReach.length > 0 && !whoDidYouReach.includes('NO_ONE'),
      then: Yup.string().nullable().required('How did you reach them? is required.'),
      otherwise: Yup.string().nullable().notRequired()
    }),
  otherProviderName: Yup.string().when('billingProviderId', {
    is: (billingProviderId: string) => billingProviderId === '-1',
    then: Yup.string().required('Provider Name is required.'),
    otherwise: Yup.string().notRequired()
  }),
  whoWereYouTryingToReach: Yup.string().when('whoDidYouReach', {
    is: (whoDidYouReach: string[]) => whoDidYouReach.includes('NO_ONE'),
    then: Yup.string()
      .min(1, 'Who were you trying to reach? is required.')
      .required('Who were you trying to reach? is required.'),
    otherwise: Yup.string().notRequired()
  }),
  howDidYouTryThem: Yup.string().when(['whoWereYouTryingToReach', 'whoDidYouReach', 'noteType'], {
    is: (whoWereYouTryingToReach: string, whoDidYouReach: string[], noteType: string) => {
      return (
        whoWereYouTryingToReach !== '' &&
        whoWereYouTryingToReach != null &&
        whoWereYouTryingToReach !== 'WORK_ON_MY_CLIENTS_BEHALF'
      );
    },
    then: Yup.string().required('How did you try them? is required.'),
    otherwise: Yup.string().notRequired()
  }),
  whatTypeOfPlaceWereYouIn: Yup.string().when(['howDidYouTryThem', 'howDidYouReachThem'], {
    is: (howDidYouTryThem: string, howDidYouReachThem: string) => {
      return howDidYouTryThem === 'IN_PERSON' || howDidYouReachThem === 'IN_PERSON';
    },
    then: Yup.string().required('What type of place were you in? is required.'),
    otherwise: Yup.string().notRequired()
  }),
  siteId: Yup.number()
    .nullable()
    .when(['whatTypeOfPlaceWereYouIn', 'sitesEnabled', 'whoDidYouReach'], {
      is: (whatTypeOfPlaceWereYouIn: string, sitesEnabled: boolean, whoDidYouReach: string[]) => {
        return (
          whatTypeOfPlaceWereYouIn !== '' &&
          whatTypeOfPlaceWereYouIn != null &&
          whatTypeOfPlaceWereYouIn !== 'CLIENT_HOME' &&
          sitesEnabled &&
          !whoDidYouReach.includes('NO_ONE')
        );
      },
      then: Yup.number().nullable().required('Site is required'),
      otherwise: Yup.number().nullable().notRequired()
    }),
  zCodes: Yup.array()
    .of(
      Yup.object({
        id: Yup.number().nullable().required('SDOH Z codes is required'),
        selectedExplanations: Yup.array()
          .of(Yup.number())
          .min(1, 'Explain SDOH Z codes is required.')
          .required('Explain SDOH Z codes is required.')
      })
    )
    .when(['typeOfSupport', 'isBillingEnabled'], {
      is: (typeOfSupport: string[], isBillingEnabled: boolean) =>
        typeOfSupport.some((type) => type !== 'NONE') && isBillingEnabled,
      then: (schema) =>
        schema.min(1, 'SDOH Z codes is required.').required('SDOH Z codes is required.')
    }),
  managerId: Yup.string()
    .nullable()
    .when('noteType', {
      is: (noteType: string) => noteType === 'MANAGER_REVIEW',
      then: Yup.string().nullable().required('Manager is required.'),
      otherwise: Yup.string().nullable().notRequired()
    }),
  chwId: Yup.string()
    .nullable()
    .when('noteType', {
      is: (noteType: string) => noteType === 'MANAGER_REVIEW',
      then: Yup.string().nullable().required('CHW is required.'),
      otherwise: Yup.string().nullable().notRequired()
    }),
  billingProviderId: Yup.string()
    .nullable()
    .test('billingProviderId', 'Provider is required.', (value, context) => {
      const noteType = context.parent.noteType;
      const whoDidYouReach = context.parent.whoDidYouReach;
      if (noteType === 'MANAGER_REVIEW' && (value === '' || value == null)) {
        return true;
      }
      if (whoDidYouReach.includes('PROVIDER') && (value === '' || value == null)) {
        return false;
      }

      return true;
    })
});

export const sortTypesOfSupportValues = (val: string[] | string, sortOrder: any[]): string[] => {
  if (Array.isArray(val)) {
    return sortOrder.filter((item) => val.includes(item.id.toString())).map((item) => item.id);
  } else {
    return [val];
  }
};
